/**
 * @fileoverview Compose full viewing and selected-version actions around a workspace-owned document. `DocumentMedia.js` owns format-specific display and `documentSources.js` owns source labels. Parent callbacks own downloads, editing, and review actions; opening a viewer does not replace source authorization or select a different canonical revision.
 */

import React, { useEffect, useRef } from 'react';
import AppIcon from '../shared/AppIcon';
import DocumentFileTypeIcon, {
  documentFileType,
  documentTypeLabel,
} from './DocumentFileTypeIcon';
import {
  documentDownloadSource,
  documentPreviewSource,
} from './documentSources';
import DocumentMedia from './DocumentMedia';
import { formatCreated, formatSize } from './archiveReviewUtils';
import './DocumentFileViewerModal.css';



function DocumentFileViewerModal({ document, onClose }) {
  const closeButtonRef = useRef(null);
  const isOpen = Boolean(document);
  useEffect(

  () => {
    if (!isOpen) return undefined;
    const opener = window.document.activeElement;
    closeButtonRef.current?.focus();
    return () => { if (opener?.isConnected) opener.focus(); };
  }, [isOpen]);

  useEffect(

  () => {
    if (!document) return undefined;
    const closeOnEscape =

    (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [document, onClose]);

  if (!document) return null;
  const filename = document.filename || document.name || 'Untitled document';
  const type = documentFileType(document);
  const source = documentPreviewSource(document, type);
  const downloadSource = documentDownloadSource(document);
  const modified = formatCreated(document.modified_at || document.updated_at || document.first_seen_at);
  const size = formatSize(document.size_bytes ?? document.size);
  const meta = [documentTypeLabel(document)];
  if (size !== '-') meta.push(size);
  if (modified.date && modified.date !== '-') meta.push(modified.date);

  return (
    <div
      className="document-file-viewer__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <section
        className={`document-file-viewer is-${type}`}
        role="dialog"
        aria-modal="true"
        aria-label={`Preview ${filename}`}>
        <header>
          <DocumentFileTypeIcon file={document} aria-hidden="true" />
          <span>
            <strong title={filename}>{filename}</strong>
            <small>{meta.join(' · ')}</small>
          </span>
          {downloadSource && (
            <a href={downloadSource} download title="Download original" aria-label={`Download ${filename}`}>
              <AppIcon icon="pi pi-download" aria-hidden="true" />
            </a>
          )}
          <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Close preview" title="Close">
            <AppIcon icon="pi pi-times" aria-hidden="true" />
          </button>
        </header>
        <div className="document-file-viewer__body">
          <DocumentMedia document={document} type={type} source={source} imageClassName="document-file-viewer__image" />
        </div>
      </section>
    </div>
  );
}

export default DocumentFileViewerModal;
